import React, { useRef } from 'react';
import { 
  Paper, 
  Typography, 
  Grid, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Box
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Styles pour le PDF au format A4
const pdfStyles = {
  paper: {
    padding: '20px',
    maxWidth: '210mm',
    margin: '0 auto',
    minHeight: '297mm',
    backgroundColor: '#ffffff'
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px'
  },
  section: {
    marginBottom: '20px'
  },
  footer: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #ccc',
    textAlign: 'center'
  }
};

const ServiceSheetSummary = ({ serviceSheet, establishmentInfo, onClose }) => {
  // Calcul des heures travaillées en présence d'élèves
  const heuresPresenceEleves = serviceSheet.semainesTravaillees * serviceSheet.heuresParSemaine || 0;
  
  // Calcul des heures travaillées hors présence d'élèves (permanences)
  const heuresPermanences = serviceSheet.heuresDues - heuresPresenceEleves;
  
  // Calcul du nombre de jours travaillés (8h par jour)
  const joursPresenceEleves = Math.round(heuresPresenceEleves / 8);
  // Arrondir au nombre inférieur pour les permanences
  const joursPermanences = Math.floor(heuresPermanences / 8);
  
  // Calcul du total des jours travaillés
  const totalJoursTravailles = joursPresenceEleves + joursPermanences;
  
  // Calcul des heures totales
  const totalHeuresTravaillees = heuresPresenceEleves + heuresPermanences;
  
  // Référence au contenu à convertir en PDF
  const contentRef = useRef(null);
  
  // Fonction pour générer le PDF
  const generatePDF = async () => {
    if (!contentRef.current) return;
    
    try {
      // Afficher un message de chargement
      alert('Génération du PDF en cours...');
      
      // Créer un nouveau document PDF au format A4
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Convertir le contenu HTML en canvas
      const canvas = await html2canvas(contentRef.current, {
        scale: 2, // Meilleure qualité
        useCORS: true,
        logging: false
      });
      
      // Obtenir les dimensions du canvas
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = canvas.height * imgWidth / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Ajouter la première page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Ajouter des pages supplémentaires si nécessaire
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Générer le nom du fichier
      const fileName = `Fiche_Service_${serviceSheet.annee}_${serviceSheet.annee + 1}.pdf`;
      
      // Télécharger le PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Une erreur est survenue lors de la génération du PDF.');
    }
  };

  return (
    <Paper sx={pdfStyles.paper} ref={contentRef}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={onClose}>Fermer</Button>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<PictureAsPdfIcon />} 
            onClick={generatePDF}
            sx={{ mr: 1 }}
          >
            Générer PDF
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={() => window.print()}
          >
            Imprimer
          </Button>
        </Box>
      </Box>
      
      <Typography variant="h4" align="center" gutterBottom>
        FICHE DE SERVICE ANNUEL
      </Typography>
      
      <Typography variant="h5" align="center" gutterBottom>
        Année scolaire {serviceSheet.annee}-{serviceSheet.annee + 1}
      </Typography>
      
      <Divider sx={{ my: 3 }} />
      
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">
            <strong>Établissement :</strong> {establishmentInfo?.name || ''}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body1" align="right">
            <strong>Date d'édition :</strong> {format(new Date(), 'dd MMMM yyyy', { locale: fr })}
          </Typography>
        </Grid>
      </Grid>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        OBLIGATIONS HORAIRES
      </Typography>
      
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="right"><strong>Valeur</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Heures de base annuelles</TableCell>
              <TableCell align="right">{serviceSheet.heuresBase} h</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Semaines travaillées</TableCell>
              <TableCell align="right">{serviceSheet.semainesTravaillees} semaines</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Heures par semaine</TableCell>
              <TableCell align="right">{serviceSheet.heuresParSemaine} h</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Heures calculées (semaines × heures/semaine)</TableCell>
              <TableCell align="right">{serviceSheet.semainesTravaillees * serviceSheet.heuresParSemaine} h</TableCell>
            </TableRow>
            {serviceSheet.personnel?.horaireDecale && (
              <>
                <TableRow>
                  <TableCell><strong>Horaire décalé</strong></TableCell>
                  <TableCell align="right">Oui</TableCell>
                </TableRow>
                {serviceSheet.personnel?.observationHoraire && (
                  <TableRow>
                    <TableCell>Observation</TableCell>
                    <TableCell align="right">{serviceSheet.personnel.observationHoraire}</TableCell>
                  </TableRow>
                )}
              </>
            )}
            <TableRow>
              <TableCell>Jours fériés</TableCell>
              <TableCell align="right">
                {(() => {
                  // Utiliser heuresFeriesParJour s'il existe, sinon utiliser 8 par défaut
                  const heuresParJour = serviceSheet.heuresFeriesParJour || 8;
                  const joursFeries = serviceSheet.heuresFeries / heuresParJour;
                  // Arrondir au nombre supérieur si la partie décimale est supérieure à 0.5
                  const joursArrondis = Math.floor(joursFeries) + (joursFeries % 1 > 0.5 ? 1 : Math.ceil(joursFeries % 1));
                  return `${joursArrondis} jours (${serviceSheet.heuresFeries} h, ${heuresParJour}h/j)`;
                })()}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Heures fractionnées</TableCell>
              <TableCell align="right">{serviceSheet.heuresFractionnees || 0} h</TableCell>
            </TableRow>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Heures dues</strong></TableCell>
              <TableCell align="right"><strong>{serviceSheet.heuresDues} h</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        RÉPARTITION DES JOURS TRAVAILLÉS
      </Typography>
      
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell align="right"><strong>Jours</strong></TableCell>
              <TableCell align="right"><strong>Heures</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Jours travaillés en présence d'élèves</TableCell>
              <TableCell align="right">{joursPresenceEleves}</TableCell>
              <TableCell align="right">{heuresPresenceEleves} h</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Jours travaillés hors présence d'élèves (permanences)</TableCell>
              <TableCell align="right">{joursPermanences}</TableCell>
              <TableCell align="right">{heuresPermanences} h</TableCell>
            </TableRow>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell align="right"><strong>{totalJoursTravailles}</strong></TableCell>
              <TableCell align="right"><strong>{totalHeuresTravaillees} h</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      {serviceSheet.permanences && serviceSheet.permanences.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            DÉTAIL DES PERMANENCES
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Mois</strong></TableCell>
                  <TableCell align="right"><strong>Jours</strong></TableCell>
                  <TableCell align="right"><strong>Heures</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceSheet.permanences.map((permanence, index) => (
                  <TableRow key={index}>
                    <TableCell>{permanence.mois}</TableCell>
                    <TableCell align="right">{permanence.jours}</TableCell>
                    <TableCell align="right">{permanence.jours * 8} h</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      
      {serviceSheet.vacancesPeriodes && serviceSheet.vacancesPeriodes.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            PÉRIODES DE VACANCES INCLUSES
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Nom</strong></TableCell>
                  <TableCell><strong>Période</strong></TableCell>
                  <TableCell align="right"><strong>Jours ouvrés</strong></TableCell>
                  <TableCell align="right"><strong>Permanences</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceSheet.vacancesPeriodes.map((periode) => (
                  <TableRow key={periode.id}>
                    <TableCell>{periode.nom}</TableCell>
                    <TableCell>
                      {periode.debut instanceof Date 
                        ? format(periode.debut, 'dd/MM/yyyy', { locale: fr })
                        : format(new Date(periode.debut), 'dd/MM/yyyy', { locale: fr })
                      } - {
                        periode.fin instanceof Date 
                        ? format(periode.fin, 'dd/MM/yyyy', { locale: fr })
                        : format(new Date(periode.fin), 'dd/MM/yyyy', { locale: fr })
                      }
                    </TableCell>
                    <TableCell align="right">{periode.joursOuvres}</TableCell>
                    <TableCell align="right">{periode.permanences || 0} jours</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={2}><strong>Total jours de vacances</strong></TableCell>
                  <TableCell align="right"><strong>{serviceSheet.totalJoursVacances || 0}</strong></TableCell>
                  <TableCell align="right"><strong>Total permanences: {serviceSheet.vacancesPeriodes.reduce((total, p) => total + (p.permanences || 0), 0)} jours</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      
      <Box sx={{ mt: 6, mb: 2, pt: 4, borderTop: '1px solid #ccc' }}>
        <Typography variant="body2" color="textSecondary" align="center">
          Document généré le {format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}
        </Typography>
      </Box>
    </Paper>
  );
};

export default ServiceSheetSummary;
